<template>
  <div class="app-carousel-wrapper">
    <div class="app-carousel">
      <img
        v-if="showControls"
        :src="ICON_ARROW_CONTROL_LEFT_ROUNDED"
        class="app-carousel-arrow-control"
        alt="carousel left control"
        @click="slide('prev')"
      />
      <div
        v-if="!loading"
        ref="carouselContainer"
        class="app-carousel-inner"
        :class="carouselClass"
        :style="carouselStyles"
      >
        <transition
          :name="transitionName"
        >
          <AppCarouselItem
            v-if="currentSlide"
            :key="activeSlide"
            :index="activeSlide"
            :vnodes="currentSlide"
            :items-per-slide="itemsPerSlide"
            :register-touch-listeners="registerTouchListeners"
            :orientation="orientation"
            :click-handler="emitClick"
          />
        </transition>
      </div>
      <img
        v-if="showControls"
        class="app-carousel-arrow-control"
        :src="ICON_ARROW_CONTROL_RIGHT_ROUNDED"
        alt="carousel right control"
        @click="slide('next')"
      />
    </div>
    <div
      v-if="showDotControls"
      class="app-carousel-controls"
    >
      <img
        v-if="showMobileControls"
        :src="ICON_ARROW_CONTROL_LEFT"
        class="app-carousel-arrow-control app-carousel-arrow-control-mobile"
        alt="carousel left control"
        @click="slide('prev')"
      />
      <div
        v-for="(item, index) in slidesCount"
        :key="item"
        class="app-carousel-control"
        :class="{
          'app-carousel-control-active': index === activeSlide,
          'app-carousel-control-disabled': disableDotControls,
        }"
        @click="setActiveSlide(index)"
      />
      <img
        v-if="showMobileControls"
        :src="ICON_ARROW_CONTROL_RIGHT"
        class="app-carousel-arrow-control app-carousel-arrow-control-mobile"
        alt="carousel right control"
        @click="slide('next')"
      />
    </div>
  </div>
</template>

<script>
import AppCarouselItem from '~/components/AppCarouselItem.vue';
import {
  ICON_ARROW_CONTROL_LEFT,
  ICON_ARROW_CONTROL_RIGHT,
  ICON_ARROW_CONTROL_LEFT_ROUNDED,
  ICON_ARROW_CONTROL_RIGHT_ROUNDED,
} from '~/rails_assets';

const CAROUSEL_BREAKPOINTS = {
  mobile: 556,
  tablet: 804,
};
const CAROUSEL_BASE_HEIGHT = {
  vertical: 200,
  horizontal: 191,
};

export default {
  name: 'AppCarousel',
  components: {
    AppCarouselItem,
  },
  emits: ['slide-click', 'click'],
  data: () => ({
    loading: true,
    activeSlide: 0,
    slideDirection: 'right',
    widgetWidth: 0,
    resizeObserver: null,
    resizing: true,
    resizeTimeout: null,
    ICON_ARROW_CONTROL_LEFT_ROUNDED,
    ICON_ARROW_CONTROL_RIGHT_ROUNDED,
    ICON_ARROW_CONTROL_LEFT,
    ICON_ARROW_CONTROL_RIGHT,
  }),
  computed: {
    // Split the list of elements passed to the slot
    // Default chunk(items per slide) size for the desktop resolution is 3
    // Inside the this.$slots.default we have an array of VNodes we pass to the AppCarouselItem component
    slides() {
      const rawItems = this.$slots?.default() ||[];
      // In Vue 3, slots may return fragment vnodes — flatten one level
      const items = rawItems.flatMap((vnode) =>
        vnode.type === Symbol.for('v-fgt') && Array.isArray(vnode.children) ? vnode.children : [vnode],
      );
      const slides = [];
      const chunkSize = this.itemsPerSlide;

      for (let i = 0; i < items.length; i += chunkSize) {
        slides.push(items.slice(i, i + chunkSize));
      }

      return slides;
    },
    currentSlide() {
      return this.slides[this.activeSlide] || null;
    },
    transitionName() {
      return this.resizing ? '' : `slide-fade-${this.slideDirection}`;
    },
    orientation() {
      return this.widgetWidth < CAROUSEL_BREAKPOINTS.mobile ? 'vertical' : 'horizontal';
    },
    itemsPerSlide() {
      if (this.orientation === 'vertical') {
        return 1;
      }

      return this.orientation === 'horizontal' && this.widgetWidth < CAROUSEL_BREAKPOINTS.tablet ? 2 : 3;
    },
    slidesCount() {
      return this.slides.length;
    },
    // Strict height setting, we need it to prevent the carousel from jumping
    // TODO: find a better solution, based on prop
    carouselClass() {
      return this.orientation === 'horizontal' ? 'app-carousel-inner-horizontal' : 'app-carousel-inner-vertical';
    },
    carouselStyles() {
      return {
        height:
          this.orientation === 'vertical'
            ? `${this.slides[0].length * CAROUSEL_BASE_HEIGHT.vertical}px`
            : `${CAROUSEL_BASE_HEIGHT.horizontal}px`,
      };
    },
    showControls() {
      return this.orientation === 'horizontal' ? this.slidesCount > 1 : false;
    },
    showMobileControls() {
      return this.orientation === 'horizontal' ? false : this.slidesCount > 1;
    },
    showDotControls() {
      return this.slidesCount > 1;
    },
    disableDotControls() {
      return this.orientation === 'vertical';
    },
    // Condition to register touch listeners for the mobile screens
    registerTouchListeners() {
      return this.orientation === 'vertical' && this.slidesCount > 1;
    },
  },
  mounted() {
    this.setupResizeObserver();
    this.loading = false;
  },
  beforeUnmount() {
    this.stopResizeObserver();
  },
  methods: {
    setActiveSlide(index) {
      if (index < 0 || index >= this.slidesCount || this.orientation === 'vertical') {
        return;
      }
      this.slideDirection = index > this.activeSlide ? 'next' : 'prev';
      this.activeSlide = index;
    },
    slide(slideDirection) {
      if (slideDirection === 'prev') {
        this.slideDirection = slideDirection;
        this.activeSlide = this.activeSlide === 0 ? this.slidesCount - 1 : this.activeSlide - 1;
      } else {
        this.slideDirection = slideDirection;
        this.activeSlide = this.activeSlide === this.slidesCount - 1 ? 0 : this.activeSlide + 1;
      }

      this.$emit('slide-click', slideDirection);
    },
    emitClick(index, e) {
      this.$emit('click', index, e);
    },
    onResize(width) {
      this.widgetWidth = width;
      this.setActiveSlide(0);
      this.resizing = false;
    },

    setupResizeObserver() {
      this.resizeObserver = new ResizeObserver(() => {
        clearTimeout(this.resizeTimeout);

        // We need to set the resizing flag to true to disable animation while resizing/changing items per slide
        this.resizing = true;
        this.resizeTimeout = setTimeout(() => {
          this.onResize(this.$el.clientWidth);
        }, 50);
      });

      this.resizeObserver.observe(this.$el);
    },
    stopResizeObserver() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }
    },
  },
};
</script>
